import React, { useCallback, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ProductIconList from 'components/@common/ProductIconList';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { TFunction } from 'i18next';
import { ProductWithCurriculum, StudentRowDataProps } from 'types';
import { sortGrade } from 'utilities/grade-util';

export interface StudentRowData extends Omit<StudentRowDataProps, 'products' | 'color'> {
  products: ProductWithCurriculum[];
}

export interface StudentSelectTableProps {
  data: Array<StudentRowData>;
  checkMap: { [key: string]: boolean };
  handleCheck?: React.ChangeEventHandler<HTMLInputElement>;
  readOnly?: boolean;
}

const StudentSelectTable: React.FC<StudentSelectTableProps> = ({
  data,
  handleCheck,
  checkMap,
  readOnly
}) => {
  const { t } = useTranslation('StudentSelectTable');
  const getColumns: (t: TFunction) => ColumnProps<any>[] = useCallback(
    (t: TFunction) => [
      {
        id: 'firstName',
        label: t('First Name'),
        sortKey: 'firstName',
        getValue: (item): string => item.firstName,
        sortable: true
      },
      {
        id: 'lastName',
        label: t('Last Name'),
        sortKey: 'lastName',
        getValue: (item): string => item.lastName,
        sortable: true
      },
      {
        id: 'username',
        label: t('Username'),
        sortKey: 'username',
        getValue: (item): string => item.username,
        sortable: true
      },
      {
        id: 'email',
        label: t('Email Address'),
        sortKey: 'email',
        getValue: (item): string => item.email,
        sortable: true
      },
      {
        id: 'gradeLevel',
        label: t('Grade'),
        sortKey: 'gradeLevel',
        sortFn: (a: any, b: any): number => sortGrade(a.gradeLevel, b.gradeLevel),
        getValue: (item): string => item.gradeLevel,
        sortable: true
      },
      {
        id: 'products',
        label: t('Products'),
        // eslint-disable-next-line react/display-name
        getValue: (item): ReactElement => <ProductIconList products={item.products} />
      }
    ],
    []
  );

  const columns: ColumnProps<any>[] = getColumns(t);

  return (
    <LwtTable
      columns={columns}
      data={data}
      checkboxMap={checkMap}
      onCheckboxChange={handleCheck}
      options={{ checkbox: true, sortable: true, readOnly }}
    />
  );
};

export default StudentSelectTable;
